import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Search, Library, Plus, Heart, Settings, Users, Music, Music2, Menu, X, Clock, ChevronDown, LogOut } from 'lucide-react';

const Sidebar = ({ user, isAdmin, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [playlists, setPlaylists] = React.useState([]);
  const [showAllPlaylists, setShowAllPlaylists] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);
  const [userVibes, setUserVibes] = React.useState([]);
  
  React.useEffect(() => {
    if (user) {
      fetchPlaylists();
      fetchUserVibes();
    }
  }, [user]);

  // Listen for playlist changes (e.g., when a playlist is cloned)
  React.useEffect(() => {
    const handlePlaylistChange = () => {
      if (user) {
        fetchPlaylists();
      }
    };

    window.addEventListener('playlistsChanged', handlePlaylistChange);
    return () => window.removeEventListener('playlistsChanged', handlePlaylistChange);
  }, [user]);
  
  const fetchPlaylists = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/playlists/me`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setPlaylists(data);
      }
    } catch (error) {
      console.error('Failed to fetch playlists:', error);
    }
  };

  const fetchUserVibes = async () => {
    try {
      // Fetch user's recent listening history
      const historyResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/listening-history/recent?limit=50`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );

      if (!historyResponse.ok) {
        console.log('No listening history available');
        return;
      }

      const recentSongs = await historyResponse.json();
      
      // Fetch all available vibes
      const vibesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vibes`);
      if (!vibesResponse.ok) {
        console.error('Failed to fetch vibes');
        return;
      }
      
      const allVibes = await vibesResponse.json();
      
      // Extract unique vibe IDs from user's listening history
      const listenedVibeIds = new Set();
      recentSongs.forEach(song => {
        if (song.vibes && Array.isArray(song.vibes)) {
          song.vibes.forEach(vibeId => listenedVibeIds.add(vibeId));
        }
      });
      
      // Filter vibes to only those the user has listened to
      const filteredVibes = allVibes
        .filter(vibe => listenedVibeIds.has(vibe.id))
        .slice(0, 5); // Limit to top 5 vibes
      
      setUserVibes(filteredVibes);
    } catch (error) {
      console.error('Failed to fetch user vibes:', error);
    }
  };

  const createPlaylist = async () => {
    if (!user) {
      if (window.confirm('Login or create a free account to use this feature. Go to login page?')) {
        window.location.href = '/login';
      }
      return;
    }
    
    const name = prompt('Enter playlist name:');
    if (!name) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/playlists`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({ name, description: '' })
        }
      );
      
      if (response.ok) {
        fetchPlaylists();
      }
    } catch (error) {
      console.error('Failed to create playlist:', error);
      alert('Failed to create playlist. Please try again.');
    }
  };
  
  const isActive = (path) => {
    return location.pathname === path;
  };

  const navItems = [
    { icon: Home, label: 'Home', path: '/' },
    { icon: Search, label: 'Search', path: '/search' },
    { icon: Library, label: 'Your Library', path: '/library' },
    { icon: Clock, label: 'History', path: '/history' },
    { icon: Users, label: 'Social', path: '/social' },
  ];
  
  const exploreItems = [
    { icon: Music, label: 'Vibes', path: '/vibes' },
    { icon: Music2, label: 'Genres', path: '/genres' },
    { icon: Users, label: 'Explore Playlists', path: '/explore-playlists' },
  ];

  const adminNavItems = (isAdmin || user?.is_admin || user?.name === 'MetaSoul Admin') ? [
    { icon: Settings, label: 'Admin Panel', path: '/admin' }
  ] : [];

  // Filter playlists to show only first 5 (or all if expanded)
  const displayedPlaylists = showAllPlaylists ? playlists : playlists.slice(0, 5);

  return (
    <>
      {/* Mobile Menu Button - Only visible on mobile */}
      <button 
        className="mobile-menu-button"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        style={{
          position: 'fixed',
          top: '20px',
          left: '20px',
          zIndex: 1001,
          background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
          border: 'none',
          borderRadius: '8px',
          padding: '12px',
          cursor: 'pointer',
          display: 'none',
          boxShadow: '0 4px 12px rgba(157, 78, 221, 0.4)'
        }}
      >
        {isMobileMenuOpen ? <X size={24} color="white" /> : <Menu size={24} color="white" />}
      </button>

      {/* Overlay for mobile - Only visible when menu is open on mobile */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            zIndex: 999,
            display: 'none'
          }}
        />
      )}

      <div className={`sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        {/* Logo Section */}
        <div className="sidebar-logo">
          <img 
            src="https://customer-assets.emergentagent.com/job_77252820-ffcc-44ae-9911-934317cd0f3d/artifacts/9m1db118_MetaSoul%20Music%20Logo%20SQUARE%20larger%20words.png" 
            alt="MetaSoul® Music" 
            className="logo-image"
          />
        </div>

        {/* Desktop-only Login/Logout Button */}
        {user ? (
          <button
            onClick={onLogout}
            className="desktop-auth-button"
            style={{
              display: 'none',
              width: 'calc(100% - 32px)',
              margin: '0 16px 16px 16px',
              padding: '12px 16px',
              background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(157, 78, 221, 0.3)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(157, 78, 221, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(157, 78, 221, 0.3)';
            }}
          >
            <LogOut size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Logout
          </button>
        ) : (
          <Link
            to="/login"
            className="desktop-auth-button"
            style={{
              display: 'none',
              width: 'calc(100% - 32px)',
              margin: '0 16px 16px 16px',
              padding: '12px 16px',
              background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              textDecoration: 'none',
              textAlign: 'center',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(157, 78, 221, 0.3)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(157, 78, 221, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(157, 78, 221, 0.3)';
            }}
          >
            Sign In
          </Link>
        )}

      {/* Mobile-only Login/Logout Button at Top */}
      <div className="mobile-top-auth" style={{
        display: 'none',
        padding: '0 16px 16px 16px'
      }}>
        {user ? (
          <button
            onClick={() => {
              onLogout();
              setIsMobileMenuOpen(false);
            }}
            style={{
              width: '100%',
              padding: '12px 16px',
              background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <LogOut size={16} />
            Logout
          </button>
        ) : (
          <Link
            to="/login"
            onClick={() => setIsMobileMenuOpen(false)}
            style={{
              width: '100%',
              padding: '12px 16px',
              background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            Sign In
          </Link>
        )}
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {/* Admin Navigation First */}
        {adminNavItems.map(({ icon: Icon, label, path }) => (
          <Link
            key={path}
            to={path}
            className={`nav-item admin-nav-item ${isActive(path) ? 'active' : ''}`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <Icon size={24} />
            <span>{label}</span>
          </Link>
        ))}
        
        {/* Regular Navigation */}
        {navItems.map(({ icon: Icon, label, path, badge }) => (
          <Link
            key={path}
            to={path}
            className={`nav-item ${isActive(path) ? 'active' : ''}`}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <Icon size={24} />
            <span>{label}</span>
            {badge && (
              <span style={{
                marginLeft: 'auto',
                fontSize: '10px',
                padding: '2px 6px',
                background: '#FFD700',
                color: '#000',
                borderRadius: '4px',
                fontWeight: '700'
              }}>
                {badge}
              </span>
            )}
          </Link>
        ))}
        
        {/* Explore Section */}
        <div style={{ 
          marginTop: '24px', 
          paddingTop: '16px', 
          borderTop: '1px solid rgba(255,255,255,0.1)' 
        }}>
          <div style={{ 
            padding: '8px 16px', 
            fontSize: '12px', 
            fontWeight: '600', 
            color: '#999', 
            textTransform: 'uppercase' 
          }}>
            Explore
          </div>
          {exploreItems.map(({ icon: Icon, label, path }) => (
            <Link
              key={path}
              to={path}
              className={`nav-item ${isActive(path) ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Icon size={24} />
              <span>{label}</span>
            </Link>
          ))}
        </div>
      </nav>

      {/* Playlists Section */}
      <div className="sidebar-section">
        <button 
          className="create-playlist-btn"
          onClick={createPlaylist}
        >
          <Plus size={16} />
          Create Playlist
        </button>
        
        {playlists.length > 0 && (
          <div className="playlists-list">
            {displayedPlaylists.map(playlist => (
              <Link 
                key={playlist.id} 
                to={`/playlist/${playlist.id}`} 
                className={`nav-item small ${isActive(`/playlist/${playlist.id}`) ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {playlist.name}
              </Link>
            ))}
            {playlists.length > 5 && (
              <button
                onClick={() => setShowAllPlaylists(!showAllPlaylists)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#9D4EDD',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  padding: '8px 16px',
                  textAlign: 'left',
                  width: '100%',
                  transition: 'color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.color = '#C77DFF'}
                onMouseOut={(e) => e.currentTarget.style.color = '#9D4EDD'}
              >
                {showAllPlaylists ? '← Show Less' : `Show All (${playlists.length})`}
              </button>
            )}
          </div>
        )}
        
        <div className="sidebar-divider"></div>
        
        <Link to="/liked" className={`nav-item ${isActive('/liked') ? 'active' : ''}`} onClick={() => setIsMobileMenuOpen(false)}>
          <Heart size={20} className="liked-songs-icon" />
          <span>Liked Songs</span>
        </Link>
      </div>

      {/* Vibes Section */}
      {user && userVibes.length > 0 && (
        <div className="sidebar-section">
          <h3 className="section-title">Your Vibes</h3>
          <div className="vibes-list">
            {userVibes.map(vibe => (
              <Link 
                key={vibe.id} 
                to={`/vibe/${vibe.id}`} 
                className="nav-item small"
              >
                {vibe.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* User Section */}
      <div className="sidebar-footer">
        {user ? (
          <div className="user-section">
            <div 
              className="user-info"
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              style={{ cursor: 'pointer' }}
            >
              <div className="user-avatar">
                <span>{user.name.charAt(0).toUpperCase()}</span>
              </div>
              <div className="user-details">
                <span className="user-name">{user.name}</span>
                {isAdmin && <span className="user-role">Admin</span>}
              </div>
              <ChevronDown 
                size={16} 
                style={{ 
                  marginLeft: 'auto',
                  transform: isUserMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }} 
              />
            </div>
            
            {isUserMenuOpen && (
              <div className="user-dropdown-menu">
                <Link 
                  to="/profile" 
                  className="user-menu-item"
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  <Settings size={16} />
                  <span>Profile Settings</span>
                </Link>
                <button 
                  className="user-menu-item"
                  onClick={() => {
                    onLogout();
                    setIsUserMenuOpen(false);
                  }}
                >
                  <LogOut size={16} />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          <Link to="/login" className="login-btn">
            Sign In
          </Link>
        )}
      </div>
    </div>
    </>
  );
};

export default Sidebar;